import { authenticatedFetch } from "./authenticatedFetch";
import type {
  ContextResponse,
  DateRange,
  Envelope,
  Insights,
  Overview,
  RefreshOperation,
  TablePayload,
  Timeseries,
  Website,
  WebsiteList,
  SourceOwner,
} from "../features/analytics/types";
const prefix = "/api/v1/analytics/websites";
type Params = Record<string, string | number | undefined>;
function qs(params: Params) {
  const out = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") out.set(key, String(value));
  });
  return out.toString();
}
function read<T>(path: string, params: Params = {}, signal?: AbortSignal) {
  return authenticatedFetch<T>(`${prefix}${path}?${qs(params)}`, {
    signal,
    serviceName: "Analytics",
  });
}
function write<T>(path: string, body: unknown, method = "POST") {
  return authenticatedFetch<T>(`${prefix}${path}`, {
    method,
    body: JSON.stringify(body),
    serviceName: "Analytics",
  });
}
export const analyticsWebsites = {
  sourceOwner: (
    source: "ga4" | "gsc",
    sourceId: string,
    signal?: AbortSignal,
  ) =>
    read<SourceOwner | null>(
      "/source-owner",
      { source, source_id: sourceId },
      signal,
    ),
  list: (params: Params = {}, signal?: AbortSignal) =>
    read<WebsiteList>("", params, signal),
  get: (id: string, signal?: AbortSignal) =>
    read<Website>(`/${encodeURIComponent(id)}`, {}, signal),
  create: (
    body: Pick<
      Website,
      | "name"
      | "scope_kind"
      | "scope_value"
      | "primary_ga4_property_id"
      | "primary_gsc_site_id"
    >,
  ) => write<Website>("", body),
  update: (
    id: string,
    body: Partial<Website> & { expected_mapping_version: number },
  ) => write<Website>(`/${encodeURIComponent(id)}`, body, "PATCH"),
  bootstrap: () =>
    write<{ created?: number; imported?: number; review_required?: number }>(
      "/bootstrap",
      {},
    ),
  pair: (
    id: string,
    body: {
      source: "ga4" | "gsc";
      source_id: string;
      expected_mapping_version: number;
      expected_source_website_mapping_version: number;
    },
  ) => write<Website>(`/${encodeURIComponent(id)}/pair`, body),
  resolve: (
    kind: string,
    id: string,
    gscId: string | undefined,
    signal?: AbortSignal,
  ) =>
    read<{ website_id: string; tab: string; mapping_review_required: boolean }>(
      "/resolve-legacy",
      { kind, id, gsc_id: gscId },
      signal,
    ),
  context: (id: string, range: DateRange, signal?: AbortSignal) =>
    read<ContextResponse>(
      `/${encodeURIComponent(id)}/context`,
      { ...range },
      signal,
    ),
  report: <T = Overview | Timeseries | TablePayload>(
    id: string,
    report: string,
    range: DateRange,
    filters: Params = {},
    signal?: AbortSignal,
  ) =>
    read<Envelope<T>>(
      `/${encodeURIComponent(id)}/reports/${encodeURIComponent(report)}`,
      { ...range, ...filters },
      signal,
    ),
  insights: (id: string, range: DateRange, signal?: AbortSignal) =>
    read<Envelope<Insights>>(
      `/${encodeURIComponent(id)}/insights`,
      { ...range },
      signal,
    ),
  explain: (
    id: string,
    range: DateRange,
    version: number | string,
    requestId: string,
  ) =>
    write<Envelope<Insights>>(`/${encodeURIComponent(id)}/insights/explain`, {
      ...range,
      request_id: requestId,
      expected_dataset_version: version,
    }),
  refresh: (id: string, range: DateRange, requestId: string) =>
    write<RefreshOperation>(`/${encodeURIComponent(id)}/refresh`, {
      request_id: requestId,
      start: range.start,
      end: range.end,
      compare: range.compare,
    }),
  refreshStatus: (id: string, operation: string, signal?: AbortSignal) =>
    read<RefreshOperation>(
      `/${encodeURIComponent(id)}/refresh/${encodeURIComponent(operation)}`,
      {},
      signal,
    ),
  export: (
    id: string,
    report: string,
    range: DateRange,
    filters: Params = {},
  ) =>
    authenticatedFetch<Blob>(
      `${prefix}/${encodeURIComponent(id)}/export?${qs({ ...range, report, ...filters })}`,
      { responseType: "blob", serviceName: "Analytics" },
    ),
};
